import React from 'react';
import {RawGenericDatePicker} from '../RawGenericDatePicker';
import {Icon} from '../../../../elements/Icon/Icon';
import {FormContainer} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';
import {ErrorMessage} from 'formik';

/**
 * @param {object} param0
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 * FYI, PeriodDatePicker does not have a formik version! So make sure to always include the raw property.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the DayPicker when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Select an option'
 *   - 'Pick an date'
 *
 * Avoid text like this:
 * --- date ----
 *
 *  @param {function} [param0.onChange]
 *  Set to a callback function with one argument, which stands for the date selected by the user. The function body should call the updater function returned by the useState hook to update the state.
 *
 * @param {number} [param0.monthsShown=1]
 * Specifies the number of months to display simultaneously in the datepicker. By default, only one month is shown. Increasing this value allows users to view and select dates from multiple months at once, which can be useful for tasks such as scheduling or planning over longer periods.
 *
 * @param {Date} [param0.startDate]
 * Sets the initial selectable start date.
 *
 * @param {Date} [param0.endDate]
 * Sets the maximum selectable end date in the datepicker for date range selection.
 *
 * @param {boolean} [param0.showWeekNumbers=false]
 * Enables or disables the display of week numbers in the datepicker.
 */

export function PeriodDatePicker({
    raw,
    onChange,
    disabled,
    label,
    name,
    monthsShown,
    endDate,
    showWeekNumbers,
    validationError,
    startDate,
}) {
    return (
        <FormContainer
            disabled={disabled}
            errorMessage={
                raw ? (
                    <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                ) : (
                    <ErrorMessage name={name} component={FormErrorMessage} />
                )
            }
            label={label}
            suffix={
                <>
                    <Icon name="calendarMonth" />
                </>
            }>
            {raw ? (
                <RawGenericDatePicker
                    monthsShown={monthsShown}
                    onChange={onChange}
                    dateFormat="dd MMMM yyyy"
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    showWeekNumbers={showWeekNumbers}
                    disabled={disabled}
                    specialClass="form-datepicker--period-picker"
                />
            ) : (
                <>Formik not available</>
            )}
        </FormContainer>
    );
}
